import { useCallback, useContext, useState } from "react";
import { AxiosContext } from "contexts/AxiosContext";
import { AxiosResponse, Method } from "axios";
import { ApiBaseResponse } from "hooks/axios/interfaces";

interface ApiRequestResponse<ResponseType, BodyType = undefined>
  extends ApiBaseResponse {
  requestCallback: (body?: BodyType) => Promise<ResponseType | undefined>;
}

export function useRequest<
  ResponseType,
  ParamsType = undefined,
  BodyType = undefined
>(
  method: Method,
  url: string,
  params?: ParamsType
): ApiRequestResponse<ResponseType, BodyType> {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const { axios, loading: axiosLoading } = useContext(AxiosContext)!;

  const requestCallback = useCallback(
    async (body?: BodyType): Promise<ResponseType | undefined> => {
      if (!axiosLoading) {
        try {
          setLoading(true);
          const response = await axios.request<
            BodyType,
            AxiosResponse<ResponseType>
          >({ method, url, data: body, params });
          setError(false);
          setLoading(false);
          return Promise.resolve(response.data);
        } catch (err) {
          setError(true);
          setLoading(false);
          return Promise.reject(err);
        }
      }

      return Promise.resolve(undefined);
    },
    [axios, axiosLoading, method, params, url]
  );

  return {
    loading,
    error,
    requestCallback
  };
}
